<template>
  <div class="hs-chart-shell h-full sm:pb-6">
    <div class="p-6 hs-chart-shell__header--container">
      <div class="hs-chart-shell__header">
        <div class="w-[55%] sm:w-full">
          <dashboard-title :title="title" icon="bar-chart-2" variant="secondary" size="sm" />
          <div class="hs-chart-shell__subtitle">Interactive analytics and trend tracking</div>
        </div>
        <div class="flex gap-3 sm:w-full sm:flex-col sm:justify-between items-center">
          <hs-select :value="intervalValue" v-model="selectedInterval" :options="getIntervals" placeholder="Interval"
            @value-change="handleSelectedInterval" />
          <hs-select :isLoading="loadingOptions" :value="value" v-model="selectedType" :options="dashboardTypes"
            placeholder="Dashboard type" @value-change="handleSelectedType" />
        </div>
      </div>
      <div v-if="chartSummary" class="hs-chart-shell__summary">
        <span>Total <strong>{{ formatSummaryNumber(chartSummary.total) }}</strong></span>
        <span>Peak <strong>{{ chartSummary.peakLabel }}</strong></span>
        <span>Average <strong>{{ formatSummaryNumber(chartSummary.average) }}</strong></span>
      </div>
    </div>
    <div class="hs-chart-shell__body h-[70%] sm:h-[60%]">
      <Line :data="vueChartData" :options="chartOptions" :plugins="gradientPlugin" class="px-2 md:px-4 h-full w-full" />
    </div>
  </div>
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { capitalizeText } from "@/helper/utils";
import DashboardTitle from "./DashboardTitle.vue";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

function hexToRgba(hex, alpha) {
  const h = String(hex).replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function firstColor(colors) {
  if (Array.isArray(colors) && colors.length) return colors[0];
  return typeof colors === "string" ? colors : "#1D8348";
}

export default {
  components: { DashboardTitle, Line },
  props: {
    xtitle: { type: String, default: "" },
    ytitle: { type: String, default: "" },
    download: { type: String, default: "chart" },
    title: { type: String, default: "" },
    dashboardTypes: { type: Array, default: () => [] },
    colors: { type: [String, Array], default: "#1D8348" },
    loadingOptions: { type: Boolean, default: false },
    chartData: { type: Array, default: () => [] },
    isLoading: { type: Boolean, default: false },
    value: [String, Array],
    intervalValue: [String, Array],
  },
  data() {
    return {
      intervals: ["hourly", "daily", "monthly"],
      gradientPlugin: [
        {
          id: "areaGradient",
          beforeDraw(chart) {
            const { ctx, chartArea, data } = chart;
            if (!chartArea) return;
            data.datasets.forEach((dataset) => {
              if (!dataset.fill) return;
              const bc = dataset.borderColor;
              if (typeof bc !== "string" || !bc.startsWith("#")) return;
              const h = bc.replace("#", "");
              const r = parseInt(h.substring(0, 2), 16);
              const g = parseInt(h.substring(2, 4), 16);
              const b = parseInt(h.substring(4, 6), 16);
              const grad = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom,
              );
              grad.addColorStop(0, `rgba(${r},${g},${b},0.22)`);
              grad.addColorStop(0.5, `rgba(${r},${g},${b},0.07)`);
              grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
              dataset.backgroundColor = grad;
            });
          },
        },
      ],
    };
  },
  computed: {
    selectedType: {
      get() {
        return this.dashboardTypes[0];
      },
      set(v) {
        this.$emit("update:selected-type", v);
      },
    },
    selectedInterval: {
      get() {
        return this.intervals[1];
      },
      set(v) {
        this.$emit("update:selected-interval", v);
      },
    },
    getIntervals() {
      return this.intervals.map((i) => ({
        name: capitalizeText(i),
        value: i,
      }));
    },
    vueChartData() {
      const series = this.chartData.map((item) => ({
        ...item,
        name: capitalizeText(item.name),
      }));
      if (!series.length) return { labels: [], datasets: [] };
      const firstRaw = series[0].data || {};
      const labels = Array.isArray(firstRaw)
        ? firstRaw.map((d) => d[0])
        : Object.keys(firstRaw);
      const colorList = Array.isArray(this.colors)
        ? this.colors
        : [this.colors];
      const datasets = series.map((item, idx) => {
        const rawData = item.data || {};
        const values = Array.isArray(rawData)
          ? rawData.map((d) => d[1])
          : Object.values(rawData);
        const lineColor =
          colorList[idx % colorList.length] || firstColor(this.colors);
        return {
          label: item.name,
          data: values,
          borderColor: lineColor,
          backgroundColor: hexToRgba(lineColor, 0.13),
          fill: true,
          tension: 0.4,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: lineColor,
          pointHoverBorderWidth: 2.5,
          cubicInterpolationMode: "monotone",
        };
      });
      return { labels, datasets };
    },
    chartSummary() {
      const { labels, datasets } = this.vueChartData;
      if (!datasets.length || !datasets[0].data.length) return null;
      const values = datasets[0].data.map((v) => Number(v) || 0);
      const total = values.reduce((sum, v) => sum + v, 0);
      const maxIdx = values.indexOf(Math.max(...values));
      const peakLabel = this.formatPeakLabel(labels[maxIdx]);
      const average = total / values.length;
      return { total, peakLabel, average };
    },
    chartOptions() {
      const isDark = document.documentElement.classList.contains("dark");
      const gridColor = isDark
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.05)";
      const tickColor = isDark
        ? "rgba(255,255,255,0.45)"
        : "rgba(0,0,0,0.38)";
      const tooltipBg = isDark ? "#1e293b" : "#0f172a";
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 700,
          easing: "easeInOutQuart",
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tooltipBg,
            titleColor: "#94a3b8",
            bodyColor: "#f8fafc",
            borderColor: "rgba(255,255,255,0.1)",
            borderWidth: 1,
            padding: { x: 14, y: 10 },
            cornerRadius: 10,
            titleFont: { size: 11, weight: "500" },
            bodyFont: { size: 13, weight: "600" },
            displayColors: false,
            callbacks: {
              label(ctx) {
                return ` ${Number(ctx.parsed.y).toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: tickColor,
              font: { size: 11 },
              maxTicksLimit: 8,
              maxRotation: 0,
            },
            title: this.xtitle
              ? { display: true, text: this.xtitle, color: tickColor, font: { size: 11 } }
              : { display: false },
          },
          y: {
            grid: { color: gridColor },
            border: { display: false, dash: [4, 4] },
            ticks: {
              color: tickColor,
              font: { size: 11 },
              maxTicksLimit: 6,
              callback(val) {
                if (val >= 1_000_000)
                  return (val / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
                if (val >= 1_000)
                  return (val / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
                return val.toLocaleString();
              },
            },
            title: this.ytitle
              ? { display: true, text: this.ytitle, color: tickColor, font: { size: 11 } }
              : { display: false },
          },
        },
      };
    },
  },
  methods: {
    handleSelectedType(v) {
      this.$emit("update:selected-type", v);
    },
    handleSelectedInterval(v) {
      this.$emit("update:selected-interval", v);
    },
    formatPeakLabel(label) {
      if (!label) return "—";
      const str = String(label);
      const date = new Date(str);
      if (isNaN(date.getTime())) return str;
      if (/^\d{4}-\d{2}$/.test(str)) {
        return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      }
      if (str.includes("T") || /\d{2}:\d{2}/.test(str)) {
        return date.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
      }
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    },
    formatSummaryNumber(val) {
      if (val >= 1_000_000) return (val / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
      if (val >= 1_000) return (val / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
      return Number(val).toLocaleString();
    },
  },
};
</script>

<style scoped>
.hs-chart-shell {
  border: 1px solid var(--hs-border);
  border-radius: 24px;
  overflow: hidden;
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  box-shadow: var(--hs-shadow-card);
}

.hs-chart-shell__header--container {
    border-bottom: 1px solid color-mix(in srgb, var(--hs-border) 78%, transparent);
}

.hs-chart-shell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: linear-gradient(135deg,
      color-mix(in srgb, var(--hs-surface) 94%, transparent) 0%,
      color-mix(in srgb, var(--hs-bg-soft) 74%, transparent) 100%);
}

.hs-chart-shell__subtitle {
  margin-top: 0.35rem;
  font-size: 0.78rem;
  color: var(--hs-muted);
}

.hs-chart-shell__body {
  padding: 1rem 0.75rem 1.25rem;
  position: relative;
}

.hs-chart-shell__summary {
  align-items: center;
  color: var(--hs-muted);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.78rem;
  gap: 0.25rem 1.1rem;
  margin-top: 0.45rem;
}

.hs-chart-shell__summary strong {
  color: var(--hs-text);
  font-weight: 750;
}
</style>
